import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ConfirmDialog } from './ConfirmDialog';
import { Input } from './Input';
import {
  cancelAllConfirms,
  registerConfirmHost,
  settleConfirm,
  useConfirmQueue,
  type PendingConfirm,
} from './confirm';

/**
 * Draws the questions asked through `confirmDialog()` and `promptDialog()`
 * (./confirm.ts), one at a time, oldest first. Mounted once, at the root and
 * inside the router, beside the Toaster.
 */
export function ConfirmHost() {
  const current = useConfirmQueue((state) => state.pending[0]);
  const { pathname } = useLocation();

  useEffect(() => registerConfirmHost(), []);

  // Leaving the page answers its open questions "no" (see cancelAllConfirms).
  useEffect(() => () => cancelAllConfirms(), [pathname]);

  // Keyed by the request, so each question starts with an empty field and
  // takes focus as it opens.
  return current ? <Question key={current.id} request={current} /> : null;
}

function Question({ request }: { request: PendingConfirm }) {
  const [text, setText] = useState('');
  const accept = () => settleConfirm(request.id, true, text);

  // Hand focus back to what asked. The dialog's own hand-back remembers what
  // had focus as it opened, which for a question that waited in the queue is
  // the previous question's dialog — gone by now — and a keyboard user would
  // be dropped at the top of the page.
  useEffect(() => {
    const { opener } = request;
    return () => {
      const active = document.activeElement;
      if (opener?.isConnected && (active === null || active === document.body)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [request]);

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) settleConfirm(request.id, false);
      }}
      title={request.title}
      description={request.description}
      confirmLabel={request.confirmLabel}
      cancelLabel={request.cancelLabel}
      variant={request.variant}
      onConfirm={accept}
    >
      {request.input && (
        <Input
          label={request.input.label}
          placeholder={request.input.placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Enter answers, as it did in the browser's prompt.
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              accept();
            }
          }}
          // The browser's prompt put the cursor in its box; so does this.
          autoFocus
        />
      )}
    </ConfirmDialog>
  );
}
